import { useEffect, useRef, useState } from "react";
import {
  addStoredDemoIncome,
  deleteStoredDemoIncome,
  getStoredDemoIncome,
} from "../utils/demoStorage";
import { isDemoMode as getIsDemoMode } from "../utils/demoAuth";
import { BASE_URL } from "../utils/apiConfig";
import { buildMonthYearQuery, getMonthName } from "../utils/monthYear";

function Income({ selectedMonth, selectedYear }) {
  const sourceInputRef = useRef(null);
  const [demoMode, setDemoMode] = useState(() => getIsDemoMode());
  const [incomeList, setIncomeList] = useState([]);
  const [source, setSource] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");

    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  };

  const loadIncome = async () => {
    if (demoMode) {
      setIncomeList(getStoredDemoIncome(selectedMonth, selectedYear));
      return;
    }

    try {
      const query = buildMonthYearQuery(selectedMonth, selectedYear);
      const res = await fetch(`${BASE_URL}/api/income?${query}`, {
        headers: getAuthHeaders(),
      });

      const data = await res.json();

      if (Array.isArray(data)) {
        setIncomeList(data);
      } else {
        setIncomeList([]);
      }
    } catch {
      setIncomeList([]);
    }
  };

  useEffect(() => {
    setDemoMode(getIsDemoMode());
  }, []);

  useEffect(() => {
    loadIncome();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedYear, demoMode]);

  const addIncome = async () => {
    if (!source.trim() || !amount) return;

    setLoading(true);

    try {
      if (demoMode) {
        addStoredDemoIncome(
          {
            source,
            amount: Number(amount),
            note,
          },
          selectedMonth,
          selectedYear
        );

        setSource("");
        setAmount("");
        setNote("");
        loadIncome();
        return;
      }

      await fetch(`${BASE_URL}/api/income`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          source,
          amount: Number(amount),
          note,
          month: selectedMonth,
          year: selectedYear,
        }),
      });

      setSource("");
      setAmount("");
      setNote("");
      loadIncome();
    } catch {
      alert("Failed to add income");
    } finally {
      setLoading(false);
    }
  };

  const deleteIncome = async (id) => {
    try {
      if (demoMode) {
        deleteStoredDemoIncome(id);
        loadIncome();
        return;
      }

      await fetch(`${BASE_URL}/api/income/${id}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });

      loadIncome();
    } catch {
      alert("Failed to delete income");
    }
  };

  const handleQuickAddIncome = () => {
    if (source.trim() && amount) {
      addIncome();
      return;
    }

    sourceInputRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
    sourceInputRef.current?.focus();
  };

  const totalIncome = incomeList.reduce(
    (sum, income) => sum + Number(income.amount || 0),
    0
  );
  const sourceTotals = incomeList.reduce((totals, income) => {
    const incomeSource = income.source || "others";
    totals[incomeSource] = (totals[incomeSource] || 0) + Number(income.amount || 0);
    return totals;
  }, {});
  const sourceEntries = Object.entries(sourceTotals).sort((a, b) => b[1] - a[1]);
  const highestIncomeSource = sourceEntries[0]?.[0] || "None";
  const highestIncomeAmount = sourceEntries[0]?.[1] || 0;
  const maxSourceTotal = Math.max(
    ...sourceEntries.map(([, value]) => Number(value || 0)),
    0
  );
  const formatMoney = (value) => Number(value || 0).toLocaleString("en-IN");
  const getIncomeDate = (income) => {
    if (income.createdAt) {
      return new Date(income.createdAt).toLocaleDateString("en-IN");
    }

    return `${getMonthName(selectedMonth)} ${selectedYear}`;
  };
  const getSourceInitial = (incomeSource) =>
    (incomeSource || "income").trim().charAt(0).toUpperCase() || "I";

  return (
    <div className="page income-premium-page">
      <div className="income-topbar">
        <div>
          <div className="dashboard-title-badges">
            <span className="soft-badge">Income Center</span>
            {demoMode && <span className="demo-mode-badge">Demo Mode</span>}
          </div>
          <h2>Income</h2>
          <p>
            {demoMode
              ? "Demo sandbox income. Changes stay in local sample data only."
              : "Track salary, business income, and other earnings"}
          </p>
        </div>

        <button
          type="button"
          className="glow-button"
          onClick={handleQuickAddIncome}
          disabled={loading}
        >
          + Add Income
        </button>
      </div>

      {demoMode && (
        <div className="demo-mode-notice">
          Demo income changes are saved locally and never sent to your personal APIs.
        </div>
      )}

      <div className="income-summary-grid">
        <div className="income-summary-card total">
          <span>Total Income</span>
          <strong>{"\u20b9"}{formatMoney(totalIncome)}</strong>
          <p>All income sources</p>
        </div>

        <div className="income-summary-card entries">
          <span>Income Entries</span>
          <strong>{incomeList.length}</strong>
          <p>Recorded this month</p>
        </div>

        <div className="income-summary-card source">
          <span>Highest Income Source</span>
          <strong>{highestIncomeSource}</strong>
          <p>
            {highestIncomeAmount > 0
              ? `\u20b9${formatMoney(highestIncomeAmount)}`
              : "No source yet"}
          </p>
        </div>

        <div className="income-summary-card month">
          <span>Current Month</span>
          <strong>{getMonthName(selectedMonth)}</strong>
          <p>{selectedYear}</p>
        </div>
      </div>

      <div className="income-workspace-grid">
        <div className="income-tools-column">
          <div className="premium-panel income-panel-card income-form-card">
            <div className="panel-title-row">
              <div>
                <span className="soft-badge">Add Income</span>
                <h3>New Income Entry</h3>
              </div>
            </div>

            <div className="income-form">
              <label>
                <span>Income Source</span>
                <input
                  ref={sourceInputRef}
                  value={source}
                  onChange={(e) => setSource(e.target.value)}
                  placeholder="Salary, Freelance, Bonus"
                />
              </label>

              <label>
                <span>Amount</span>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Amount"
                />
              </label>

              <label>
                <span>Note</span>
                <input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Optional note"
                />
              </label>

              <label>
                <span>Month</span>
                <input
                  value={`${getMonthName(selectedMonth)} ${selectedYear}`}
                  readOnly
                />
              </label>

              <div className="income-form-actions">
                <button type="button" onClick={addIncome} disabled={loading}>
                  {loading ? "Adding..." : "Add Income"}
                </button>
              </div>
            </div>
          </div>

          <div className="premium-panel income-panel-card income-split-card">
            <div className="panel-title-row">
              <div>
                <span className="soft-badge">Income Split</span>
                <h3>Source Breakdown</h3>
              </div>
            </div>

            {sourceEntries.length === 0 ? (
              <p className="placeholder-text">No income split available.</p>
            ) : (
              <div className="income-source-list">
                {sourceEntries.map(([incomeSource, total]) => {
                  const percentage =
                    maxSourceTotal > 0 ? (Number(total || 0) / maxSourceTotal) * 100 : 0;

                  return (
                    <div key={incomeSource} className="income-source-row">
                      <div className="income-source-row-top">
                        <span>{incomeSource}</span>
                        <strong>{"\u20b9"}{formatMoney(total)}</strong>
                      </div>
                      <div className="income-bar-track">
                        <div
                          className="income-bar-fill"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="income-list-column">
          <div className="premium-panel income-panel-card income-list-card">
            <div className="panel-title-row">
              <div>
                <span className="soft-badge">Income Ledger</span>
                <h3>Income List</h3>
              </div>
              <p>{"\u20b9"}{formatMoney(totalIncome)}</p>
            </div>

            {incomeList.length === 0 ? (
              <p className="placeholder-text">No income added yet.</p>
            ) : (
              <div className="premium-income-list">
                {incomeList.map((income) => (
                  <div key={income._id} className="premium-income-item">
                    <div className="income-source-icon">
                      {getSourceInitial(income.source)}
                    </div>

                    <div className="premium-income-main">
                      <div className="premium-income-title-row">
                        <h4>{income.source || "Income"}</h4>
                        <strong>{"\u20b9"}{formatMoney(income.amount)}</strong>
                      </div>

                      <div className="premium-income-meta">
                        <span className="income-source-pill">
                          {income.source || "others"}
                        </span>
                        <span>{getIncomeDate(income)}</span>
                        {income.note && <span>{income.note}</span>}
                      </div>
                    </div>

                    <button
                      type="button"
                      className="delete-btn"
                      onClick={() => deleteIncome(income._id)}
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Income;
